import { defineStore } from "pinia";
import { ref, computed, onMounted } from "vue";
import api, { markTokenRefreshed } from "../services/api";
import { User } from "@/types/User";

export const useAuthStore = defineStore("auth", () => {
  // User state is now managed only by server-side cookies (authToken + refreshToken)
  // We keep user data in memory for the current session only
  const user = ref<User | null>(null);
  const tenant_id = ref<string | undefined>(undefined);

  const res = localStorage.getItem("user");
  if (res != null) {
    user.value = JSON.parse(res);
  }

  const isAuthenticated = computed(() => !!user.value);
  const userRole = computed(() => user.value?.role || null);
  const tenantId = computed(
    () => tenant_id.value || user.value?.tenant_id || null
  );
  const isSuperAdmin = computed(() => user.value?.role === "super_admin");
  const isAdmin = computed(
    () =>
      user.value?.role === "super_admin" || user.value?.role === "city_admin"
  );
  const canEdit = computed(
    () =>
      user.value?.role === "super_admin" ||
      user.value?.role === "city_admin" ||
      user.value?.role === "supervisor"
  );

  async function login(
    email: string,
    password: string,
    tenant_id_param: string
  ) {
    try {
      const response = await api.post("/auth/login", {
        email,
        password,
        tenant_id: tenant_id_param,
      });

      user.value = response.data.user;
      localStorage.setItem("user", JSON.stringify(user.value));
      tenant_id.value = tenant_id_param;

      if (user.value && !user.value.tenant_id) {
        user.value.tenant_id = tenant_id_param;
      }

      // Mark token as fresh so the request interceptor doesn't refresh immediately
      markTokenRefreshed();
      return true;
    } catch (error) {
      console.error("Login error:", error);
      logout();
      throw error;
    }
  }

  /**
   * Clear local auth state without making any API call.
   * Used when we already know the session is invalid (e.g. refresh token expired).
   */
  function clearLocalState() {
    localStorage.removeItem("user");
    user.value = null;
    tenant_id.value = undefined;
  }

  async function logout() {
    try {
      // Call logout endpoint to clear refresh token from database
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout API error:", error);
      // Continue with local cleanup even if API call fails
    }

    // Clear local state
    clearLocalState();
  }

  async function changePassword(
    current_password: string,
    new_password: string
  ) {
    if (!user.value?.id) {
      throw new Error("User not authenticated");
    }

    await api.put(`/users/${user.value.id}/change-password`, {
      current_password,
      new_password,
    });
    return true;
  }

  function setTenant(new_tenant_id: string) {
    tenant_id.value = new_tenant_id;
  }

  return {
    user,
    tenant_id,
    isAuthenticated,
    userRole,
    tenantId,
    isSuperAdmin,
    isAdmin,
    canEdit,
    login,
    logout,
    clearLocalState,
    changePassword,
    setTenant,
  };
});
